import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, Sparkles, Clock, Wallet, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * GapFixerModal
 *
 * Turns a scary red "BELOW" gap in the Score Breakdown into an actionable
 * next step. Content is gap-specific (grades, english, test_score,
 * subjects, activities, portfolio_interview) and includes:
 *  - A concrete week-by-week timeline
 *  - Typical cost range
 *  - Book a WUA+ specialist CTA (POST /api/specialists/request)
 *
 * Props:
 *   open, onOpenChange
 *   gapKey: "grades" | "english" | "test_score" | "subjects" | "activities" | "portfolio_interview"
 *   gapValue: number (negative)
 *   degreeId?: string        — for the specialist request context
 *   degreeName?: string
 *   universityName?: string
 */
const GAP_CONTENT = {
  grades: {
    heading: "Close the grades gap",
    icon: Sparkles,
    timeline: [
      { weeks: "Now", step: "Book a diagnostic mock with your current teacher — identify the 3 topics losing you marks." },
      { weeks: "Week 1–4", step: "Weekly 1:1 tutoring focused on those 3 topics + past-paper cycles." },
      { weeks: "Week 5–8", step: "Full timed past papers under exam conditions. Aim to move 1 letter grade." },
      { weeks: "Week 9–12", step: "Consolidation + retake / resit registration (if applicable)." },
    ],
    typicalUplift: "+1 A-Level letter (or +2 IB HL points) over 12 weeks",
    typicalCost: "$65–90/hr · $780–1,080 for a full 12-week package",
    specialistId: "grades-tutoring",
  },
  english: {
    heading: "Close the English proficiency gap",
    icon: Sparkles,
    timeline: [
      { weeks: "Now", step: "Take a free IELTS/TOEFL diagnostic mock (British Council or ETS)." },
      { weeks: "Week 1", step: "Book your IELTS/TOEFL test date — most centres offer weekly slots." },
      { weeks: "Week 1–4", step: "Twice-weekly coaching on Writing + Speaking (the two hardest bands to lift)." },
      { weeks: "Week 5–6", step: "Full mock cycles + writing marked by a certified examiner." },
      { weeks: "Week 6–8", step: "Sit the test. Score released 5–13 days later." },
    ],
    typicalUplift: "IELTS +1.0 band (e.g. 6.0 → 7.0) in 6–8 weeks",
    typicalCost: "$220 for a 6-week coaching package · $260 exam fee",
    specialistId: "english-fasttrack",
  },
  test_score: {
    heading: "Close the SAT / ACT gap",
    icon: Sparkles,
    timeline: [
      { weeks: "Now", step: "Sit a full-length SAT or ACT mock. Establish a baseline." },
      { weeks: "Week 1–4", step: "Weekly live coaching + 4 targeted section drills between." },
      { weeks: "Week 5–8", step: "Bi-weekly full mocks + review of every wrong answer." },
      { weeks: "Week 9–12", step: "Sit the test. Register early — top centres fill up 2 months out." },
    ],
    typicalUplift: "SAT +120 to +180 · ACT +3 to +5",
    typicalCost: "$950 for a 12-week programme · $60–68 registration fee",
    specialistId: "test-prep",
  },
  subjects: {
    heading: "Close the subject-requirements gap",
    icon: Sparkles,
    timeline: [
      { weeks: "Now", step: "Book a curriculum-strategy consultation — audit your current subject mix vs the degree's stated requirements." },
      { weeks: "Week 1", step: "Get a written action plan: add-a-subject, bridge-course, or online AP alternatives." },
      { weeks: "Week 1–4", step: "Register for the recommended bridge course (self-study CIE, MEI Further Maths, or online AP)." },
      { weeks: "Rolling", step: "Ongoing mentor check-ins to keep the new subject on-track for exam entry." },
    ],
    typicalUplift: "Unlocks Oxbridge / MIT-track programmes gated on Further Maths / HL Physics.",
    typicalCost: "$175 one-off consultation · bridge-course fees vary ($200–800 self-study)",
    specialistId: "subjects-advisor",
  },
  activities: {
    heading: "Strengthen your activities profile",
    icon: Sparkles,
    timeline: [
      { weeks: "Week 1", step: "Ideation call — pick ONE signature project (Olympiad, published research, community initiative)." },
      { weeks: "Week 2–4", step: "Design the project. Register for the Olympiad or launch the initiative." },
      { weeks: "Month 2–3", step: "Bi-weekly mentor coaching keeps momentum. Document everything for personal statement." },
      { weeks: "Month 3+", step: "Compete / publish / present. This becomes the anchor story of your application." },
    ],
    typicalUplift: "3/5 → 5/5 activities rating with a signature achievement admissions officers remember.",
    typicalCost: "$340 for a 3-month mentored programme",
    specialistId: "activities-mentor",
  },
  portfolio_interview: {
    heading: "Portfolio & interview preparation",
    icon: Sparkles,
    timeline: [
      { weeks: "Now", step: "Book a portfolio review with a subject-matter specialist (Architecture, Art, Music, Medicine)." },
      { weeks: "Week 1–2", step: "Portfolio critique + reshoot / redraft plan." },
      { weeks: "Week 3", step: "First mock interview (Oxbridge or MMI) with recorded playback." },
      { weeks: "Week 4", step: "Final mock + written feedback. You're now interview-ready." },
    ],
    typicalUplift: "Interview-confident within 4 weeks; portfolio uplift for Architecture / Art / Music.",
    typicalCost: "$260 portfolio pack (3 reviews + mock interview + written feedback)",
    specialistId: "portfolio-interview",
  },
};

export function GapFixerModal({ open, onOpenChange, gapKey, gapValue, degreeId, degreeName, universityName }) {
  const content = GAP_CONTENT[gapKey] || GAP_CONTENT.grades;
  const [requesting, setRequesting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const Icon = content.icon;

  const requestSpecialist = async () => {
    setRequesting(true);
    try {
      await api.post("/specialists/request", {
        specialist_id: content.specialistId,
        gap_context: {
          gap_key: gapKey,
          gap_value: gapValue,
          degree_id: degreeId,
          degree_name: degreeName,
          university_name: universityName,
        },
      });
      setConfirmed(true);
      toast.success("Specialist request logged — we'll email you within 24 hours.");
    } catch (e) {
      if (e?.response?.status === 401) {
        toast("Sign in to book a specialist — we'll pick up where you left off.");
      } else {
        toast.error("Could not log request. Please try again.");
      }
    } finally {
      setRequesting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid={`gap-fixer-modal-${gapKey}`}>
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <Icon className="h-5 w-5 text-[#4C1D95]" />
            <span className="label-uppercase !text-[10px] text-[#4C1D95]">WUA+ · Fix this gap</span>
          </div>
          <DialogTitle className="font-editorial text-2xl">{content.heading}</DialogTitle>
          {degreeName && universityName && (
            <DialogDescription className="text-sm text-stone-600">
              For your application to <span className="font-semibold">{universityName} — {degreeName}</span>
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="space-y-4 mt-3">
          {/* Timeline */}
          <div>
            <div className="label-uppercase !text-[10px] text-stone-600 mb-2 flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" /> Fastest path
            </div>
            <ol className="space-y-2">
              {content.timeline.map((row, i) => (
                <li key={i} className="flex gap-3 text-sm" data-testid={`gap-timeline-row-${i}`}>
                  <span className="shrink-0 font-mono text-xs uppercase tracking-wider text-stone-500 w-24 pt-0.5">
                    {row.weeks}
                  </span>
                  <span className="text-stone-800">{row.step}</span>
                </li>
              ))}
            </ol>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="rounded-md border border-stone-200 bg-stone-50 px-3 py-2">
              <div className="label-uppercase !text-[10px] text-stone-600">Typical uplift</div>
              <div className="text-sm text-stone-800 mt-0.5">{content.typicalUplift}</div>
            </div>
            <div className="rounded-md border border-stone-200 bg-stone-50 px-3 py-2">
              <div className="label-uppercase !text-[10px] text-stone-600 flex items-center gap-1.5">
                <Wallet className="h-3 w-3" /> Cost
              </div>
              <div className="text-sm text-stone-800 mt-0.5">{content.typicalCost}</div>
            </div>
          </div>

          {/* CTA */}
          <div className="rounded-md border-2 border-[#4C1D95] bg-[#4C1D95]/5 p-4 mt-4">
            {confirmed ? (
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-emerald-700 shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-emerald-900">Request logged</div>
                  <div className="text-sm text-stone-700 mt-1">
                    We'll email you within 24 hours with a matched WUA+ specialist and their next 3 available slots.
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div>
                  <div className="font-editorial text-lg">Talk to a WUA+ specialist</div>
                  <div className="text-sm text-stone-600">
                    Delivered via our Pearsana partner network. No credit card — we email you 3 slots to pick from.
                  </div>
                </div>
                <Button
                  onClick={requestSpecialist}
                  disabled={requesting}
                  data-testid={`gap-book-specialist-${gapKey}`}
                  className="bg-[#4C1D95] hover:bg-[#3B0764] text-white shrink-0">
                  {requesting
                    ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Requesting…</>
                    : <>Request specialist match</>}
                </Button>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default GapFixerModal;
